import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { services } from "../data/services";
import { SERVICE_SEO } from "../data/serviceSeo";
import { getBrandAsset } from "../lib/brandManifest";

const SITE_URL = "https://www.ekselens.com";
const SITE_NAME = "Ekselens Consulting";
const DEFAULT_OG = `${SITE_URL}/ekselens-star.png`;

// Module-level cache for admin-managed per-route social-copy overrides
// ({ "/route": { title?, description? } }). null = not yet fetched.
let _OG_COPY_CACHE = null;

/**
 * Resolve the per-route OG image URL. Points at the backend
 * `/api/og/{kind}/{slug}.png` endpoint which renders a branded
 * 1200×630 card per route, cached for 24 hours. Falls back to the
 * static brand star image on routes we haven't mapped.
 */
function resolveOgImage(pathname) {
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const seg = pathname.replace(/^\/+|\/+$/g, "").split("/");
  if (!seg[0]) return `${apiBase}/api/og/page/home.png`;
  if (seg[0] === "services" && seg[1]) return `${apiBase}/api/og/service/${seg[1]}.png`;
  if (seg[0] === "insights" && seg[1]) return `${apiBase}/api/og/insight/${seg[1]}.png`;
  if (seg[0] === "resources" && seg[1] && seg[1].endsWith("-self-assessment")) {
    const brand = seg[1].replace(/-self-assessment$/, "");
    return `${apiBase}/api/og/lite/${brand}.png`;
  }
  // Top-level static pages
  const staticPages = new Set(["services", "insights", "about", "contact", "resources"]);
  if (staticPages.has(seg[0]) && !seg[1]) return `${apiBase}/api/og/page/${seg[0]}.png`;
  // Brand-managed default OG image (admin → Brand assets, key "og-default") wins
  // over the bundled star image when published.
  return getBrandAsset("og-default") || DEFAULT_OG;
}

const PAGE_META = {
  "/": {
    title: "Ekselens Consulting | Technology-Driven Transformation Consultancy",
    desc: "Ekselens Consulting helps organisations modernise services, adopt AI responsibly, improve delivery confidence, optimise suppliers and realise measurable value from transformation.",
    keywords:
      "technology consulting UK, transformation consultancy, AI readiness consulting, ITSM consultancy, supplier optimisation consulting, benefits realisation, PMO uplift, change management consulting, EKSELENS360",
  },
  "/services": {
    title: "Consulting Services | Ekselens Consulting",
    desc: "EKSELENS360 service architecture — 12 focused consulting services across transformation, AI, PMO, ITSM, supplier optimisation, data, change and benefits realisation.",
    keywords:
      "IT transformation consulting, AI adoption consulting, PMO services, ITSM maturity, supplier contract optimisation, IT change management, benefits realisation consulting, technology transformation UK",
  },
  "/insights": {
    title: "Insights | Ekselens Consulting",
    desc: "Practical commentary on transformation, AI adoption, delivery confidence, supplier optimisation and benefits realisation from Ekselens Consulting.",
    keywords: "transformation insights, AI consulting insights, delivery management blog, technology leadership UK",
  },
  "/system-of-proof": {
    title: "Evidence Graph Maturity — System of Proof Self-Assessment | Ekselens Consulting",
    desc: "How defensible is your enterprise's system of proof? Rate your evidence trail across five pillars — Provenance, Challenge, Continuity, Control and Disclosure — in a free 25-statement self-assessment.",
    keywords: "evidence graph maturity, system of proof, reinvention assurance, transformation assurance, provenance, model risk management, audit-ready transformation, Ekselens",
  },
  "/system-of-proof-library": {
    title: "System of Proof Frameworks — Manifesto, EGMM & MRM | Ekselens Consulting",
    desc: "Download the Ekselens System of Proof doctrine: the Manifesto, the Evidence Graph Maturity Model and the Model Risk Management framework.",
    keywords: "system of proof manifesto, evidence graph maturity model, model risk management framework, MRM framework, EGMM, reinvention assurance, Ekselens frameworks",
  },
  "/about": {
    title: "About Ekselens Consulting",
    desc: "Ekselens Consulting is a UK-based consulting practice combining public-sector rigour with private-sector pace to deliver measurable transformation outcomes.",
    keywords: "Ekselens Consulting, UK consulting firm, technology transformation partner",
  },
  "/contact": {
    title: "Book a Discovery Conversation | Ekselens Consulting",
    desc: "Start with a focused discovery conversation to clarify your priorities and identify practical next steps with Ekselens Consulting.",
    keywords: "book consulting discovery call, transformation consultancy contact, IT consulting UK",
  },
  "/resources": {
    title: "Resources | Ekselens Consulting",
    desc: "Downloadable guides, frameworks and templates for transformation, AI adoption, supplier optimisation, ITSM and benefits realisation.",
    keywords: "transformation playbooks, AI readiness templates, PMO frameworks, ITSM guides, consulting downloads",
  },
  "/privacy": {
    title: "Privacy Policy | Ekselens Consulting",
    desc: "How Ekselens Consulting processes and protects your personal data under UK GDPR.",
  },
  "/cookies": {
    title: "Cookie Policy | Ekselens Consulting",
    desc: "How Ekselens Consulting uses cookies and similar technologies on this website.",
  },
  "/terms": {
    title: "Terms of Use | Ekselens Consulting",
    desc: "The terms governing your use of the Ekselens Consulting website.",
  },
  "/data-retention": {
    title: "Data Retention & Erasure | Ekselens Consulting",
    desc: "How long Ekselens Limited retains each category of personal data, the lawful basis, and how to request erasure under UK GDPR.",
    keywords: "data retention policy, GDPR erasure, data protection, Ekselens governance",
  },
  "/sub-processors": {
    title: "Sub-processors & DPA Summary | Ekselens Consulting",
    desc: "The third-party sub-processors Ekselens Limited engages, the data each processes, hosting regions, and a downloadable DPA summary for procurement.",
    keywords: "sub-processor list, data processing agreement, DPA, procurement due diligence, GDPR sub-processors",
  },
  "/help": {
    title: "How EKSELENS360 works — Help & Methodology | Ekselens Consulting",
    desc: "A user guide for the EKSELENS360 Maturity Engine — the 12 sub-brands, how the deterministic scoring works, what you get out, and how to request the methodology paper.",
    keywords: "EKSELENS360 methodology, maturity assessment guide, IT consulting framework, lite assessment help",
  },
  "/start": {
    title: "Start here — choose your free assessment | Ekselens Consulting",
    desc: "Two questions to route you to the right free, sector-calibrated diagnostic: assess one function, an end-to-end value stream, or explore the whole 360 method.",
    keywords: "free maturity assessment, start assessment, IT maturity, value stream assessment, EKSELENS360",
  },

  "/methodology": {
    title: "How the 360 family works as one method | Ekselens Consulting",
    desc: "The shared EKSELENS360 method — one maturity scale, decision-grade deliverables and an eight-phase delivery lifecycle across the 12 functional lenses and the ENTERPRISE360 reinvention engine.",
    keywords: "consulting methodology, maturity assessment method, transformation method, EKSELENS360 method, ENTERPRISE360",
  },
  "/how-it-works": {
    title: "How Ekselens delivers with certainty — the System of Proof",
    desc: "See the whole Ekselens system on one page — the System of Proof discipline, the eight-phase delivery lifecycle, 12 EKSELENS360 service lines, 12 Enterprise360 value streams, the delivery matrix, our evidence & assurance model, and what we deliberately do not do.",
    keywords: "system of proof, Ekselens methodology, eight-phase delivery lifecycle, EKSELENS360 service lines, Enterprise360 value streams, delivery matrix, transformation assurance, evidence graph maturity, consulting operating model",
  },
  "/system-of-proof-in-action": {
    title: "System of Proof in action — worked examples | Ekselens Consulting",
    desc: "See the Ekselens engine live: illustrative, evidence-led worked examples across business archetypes — current-state diagnosis, target-state blueprint, mobilisation roadmap, RAID, benefits and the System of Proof assurance layer. Plus our honest sector calibration map.",
    keywords: "system of proof, worked examples, transformation blueprint demo, evidence-led consulting, Enterprise360 example, sector calibration, target operating model, consulting proof",
  },
  "/accessibility": {
    title: "Accessibility — WCAG 2.1 AA | Ekselens Consulting",
    desc: "Ekselens Consulting's accessibility statement and WCAG 2.1 AA conformance commitment, plus how to request a VPAT or report an access barrier.",
    keywords: "accessibility statement, WCAG 2.1 AA, VPAT, accessible consulting website",
  },
  "/admin/login": {
    title: "Admin Sign-in | Ekselens Consulting",
    desc: "Restricted admin area.",
    noindex: true,
  },
  "/admin": {
    title: "Admin Dashboard | Ekselens Consulting",
    desc: "Restricted admin area.",
    noindex: true,
  },
  "/enterprise360": {
    title: "Enterprise360 — the whole-organisation reinvention engine | Ekselens Consulting",
    desc: "Enterprise360 assembles a board-grade reinvention blueprint across 12 value streams — current-state diagnosis, target operating model, mobilisation roadmap, RAID, benefits realisation and the System of Proof assurance layer.",
    keywords: "Enterprise360, reinvention blueprint, target operating model, value stream transformation, transformation assurance, mobilisation roadmap, benefits realisation, Ekselens",
  },
  "/value-stream-assessment": {
    title: "Value Stream 360 — free end-to-end value-stream assessment | Ekselens Consulting",
    desc: "A free, sector-calibrated diagnostic for an end-to-end value stream — score maturity, surface bottlenecks and get a prioritised reinvention roadmap grounded in the EKSELENS360 method.",
    keywords: "value stream assessment, value stream mapping, operating model maturity, free diagnostic, EKSELENS360, transformation roadmap, Ekselens",
  },
  "/the-360-family": {
    title: "The 360 family — one method, twelve lenses | Ekselens Consulting",
    desc: "How the EKSELENS360 family works as one coherent method — 12 functional lenses, the Enterprise360 reinvention engine and a shared maturity scale delivering decision-grade outcomes.",
    keywords: "360 family, EKSELENS360, Enterprise360, consulting method, maturity assessment, functional lenses, transformation consultancy, Ekselens",
  },
  "/case-studies": {
    title: "Case studies — proven transformation outcomes | Ekselens Consulting",
    desc: "Sector-anonymised case studies showing how Ekselens Consulting delivered measurable transformation outcomes — the situation, what changed, the delivery moves and the evidence of impact.",
    keywords: "consulting case studies, transformation outcomes, delivery evidence, technology transformation results, Ekselens case studies",
  },
  __404__: {
    title: "Page not found | Ekselens Consulting",
    desc: "The page you're looking for has moved or no longer exists.",
    noindex: true,
  },
  __reviewer__: {
    title: "Independent Reviewer View | Ekselens Consulting",
    desc: "Read-only assurance summary shared for independent review.",
    noindex: true,
  },
};

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG,
  description:
    "UK technology-driven transformation consultancy. AI readiness, delivery, supplier optimisation, ITSM, change and benefits realisation.",
  address: {
    "@type": "PostalAddress",
    addressCountry: "GB",
  },
  areaServed: "GB",
  sameAs: ["https://www.linkedin.com/company/113256920/"],
};

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    for (const [k, v] of Object.entries(attrs.create || {})) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs.set || {})) el.setAttribute(k, v);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function injectJsonLd(id, payload) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

function removeJsonLd(id) {
  const el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (el) el.remove();
}

/**
 * SeoHead — applies the right title/description/OG/Twitter/canonical/JSON-LD
 * for the current route. Also handles dynamic routes (/services/:slug,
 * /insights/:slug) by deriving meta from the underlying data.
 */
const SeoHead = () => {
  const { pathname } = useLocation();
  const url = `${SITE_URL}${pathname}`;
  const [ogOverrides, setOgOverrides] = useState(_OG_COPY_CACHE);

  // Fetch admin-managed per-route social-copy overrides once per session.
  useEffect(() => {
    if (_OG_COPY_CACHE !== null) return;
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${apiBase}/api/public/og-copy.json`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        _OG_COPY_CACHE = data || {};
        setOgOverrides(_OG_COPY_CACHE);
      })
      .catch((e) => {
        _OG_COPY_CACHE = {};
        // eslint-disable-next-line no-console
        console.warn("SeoHead og-copy fetch failed:", e);
      });
  }, []);

  const dynamicMeta = useMemo(() => {
    try {
      if (pathname.startsWith("/services/")) {
        const slug = pathname.split("/")[2];
        const s = services.find((x) => x.slug === slug);
        if (s) {
          const groupLabel =
            typeof s.group === "string"
              ? s.group
              : s.group?.title || "Consulting";
          const seo = SERVICE_SEO[slug] || {};
          const desc = seo.description || s.seoDescription || s.short || s.hero || s.intro?.slice(0, 200) || "";
          // Keep titles within Google's ~60-char SERP limit: use the explicit
          // seoTitle if set, else trim the brand suffix when the full title is
          // too long so the service name itself never gets truncated.
          const fullTitle = `${s.name} | Ekselens Consulting`;
          const title = seo.title || s.seoTitle || (fullTitle.length > 60 ? `${s.name} | Ekselens` : fullTitle);
          return {
            title,
            desc,
            keywords: seo.keywords || s.seoKeywords || `${s.name.toLowerCase()}, ${groupLabel.toLowerCase()} consulting, technology consulting UK`,
            jsonld: {
              "@context": "https://schema.org",
              "@type": "Service",
              name: s.name,
              serviceType: groupLabel,
              provider: { "@type": "ProfessionalService", name: SITE_NAME, url: SITE_URL },
              description: desc,
              url,
            },
          };
        }
      }
      if (pathname.startsWith("/resources/") && pathname.endsWith("-self-assessment")) {
        const slug = pathname.split("/")[2];
        const brand = slug.replace(/-self-assessment$/, "").toUpperCase();
        return {
          title: `${brand} Lite Self-Assessment | Ekselens Consulting`,
          desc: `Take the free ${brand} lite maturity self-assessment from EKSELENS360 — a fast, anonymous diagnostic with an instant score and tailored next steps. No sign-up required.`,
          keywords: `${brand.toLowerCase()} self-assessment, ${brand.toLowerCase()} maturity, EKSELENS360 diagnostic, free maturity assessment`,
        };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("SeoHead dynamicMeta failed:", e);
    }
    return null;
  }, [pathname, url]);

  useEffect(() => {
    try {
      const known = !!PAGE_META[pathname];
      const isReviewer = pathname.startsWith("/reviewer/");
      const isDynamicKnown =
        pathname.startsWith("/services/") || pathname.startsWith("/insights/") || pathname.startsWith("/resources/") || pathname.startsWith("/admin") || isReviewer;
      const fallback = (!known && !isDynamicKnown) ? PAGE_META.__404__ : PAGE_META["/"];
      const meta = dynamicMeta || (isReviewer ? PAGE_META.__reviewer__ : PAGE_META[pathname]) || fallback;
      // The reviewer page owns its own document.title (it shows the client name);
      // only avoid clobbering it with the 404 fallback here.
      if (!isReviewer) document.title = meta.title;

      // Admin OG-copy override (social-share title/desc only; on-page title untouched)
      const ogOverride = ogOverrides?.[pathname] || {};
      const ogTitle = ogOverride.title || meta.title;
      const ogDesc = ogOverride.description || meta.desc;

    upsertMeta('meta[name="description"]', {
      create: { name: "description" },
      set: { content: meta.desc },
    });
    if (meta.keywords) {
      upsertMeta('meta[name="keywords"]', {
        create: { name: "keywords" },
        set: { content: meta.keywords },
      });
    }
    upsertMeta('meta[name="robots"]', {
      create: { name: "robots" },
      set: { content: meta.noindex ? "noindex, nofollow" : "index, follow" },
    });

    // Canonical
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta('meta[property="og:title"]', {
      create: { property: "og:title" },
      set: { content: ogTitle },
    });
    upsertMeta('meta[property="og:description"]', {
      create: { property: "og:description" },
      set: { content: ogDesc },
    });
    upsertMeta('meta[property="og:url"]', {
      create: { property: "og:url" },
      set: { content: url },
    });
    const ogImageUrl = resolveOgImage(pathname);
    upsertMeta('meta[property="og:image"]', {
      create: { property: "og:image" },
      set: { content: ogImageUrl },
    });
    upsertMeta('meta[property="og:image:width"]', {
      create: { property: "og:image:width" },
      set: { content: "1200" },
    });
    upsertMeta('meta[property="og:image:height"]', {
      create: { property: "og:image:height" },
      set: { content: "630" },
    });
    upsertMeta('meta[property="og:type"]', {
      create: { property: "og:type" },
      set: { content: pathname.startsWith("/insights/") ? "article" : "website" },
    });
    upsertMeta('meta[property="og:site_name"]', {
      create: { property: "og:site_name" },
      set: { content: SITE_NAME },
    });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', {
      create: { name: "twitter:card" },
      set: { content: "summary_large_image" },
    });
    upsertMeta('meta[name="twitter:title"]', {
      create: { name: "twitter:title" },
      set: { content: ogTitle },
    });
    upsertMeta('meta[name="twitter:description"]', {
      create: { name: "twitter:description" },
      set: { content: ogDesc },
    });
    upsertMeta('meta[name="twitter:image"]', {
      create: { name: "twitter:image" },
      set: { content: ogImageUrl },
    });

    // JSON-LD: Organization (everywhere) — prefer a brand-managed logo if set.
    injectJsonLd("org", { ...ORG_JSONLD, logo: getBrandAsset("logo") || ORG_JSONLD.logo });

    // JSON-LD: Service (on service pages)
    if (dynamicMeta?.jsonld) {
      injectJsonLd("service", dynamicMeta.jsonld);
    } else {
      removeJsonLd("service");
    }

    // JSON-LD: WebSite + SearchAction on home
    if (pathname === "/") {
      injectJsonLd("website", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: SITE_URL,
        name: SITE_NAME,
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/insights?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      });
    } else {
      removeJsonLd("website");
    }

    // JSON-LD: the seven-beat System arc (definitive /how-it-works explainer)
    if (pathname === "/how-it-works") {
      const beats = [
        "System of Proof — the discipline and the promise",
        "The eight-phase delivery lifecycle",
        "EKSELENS360 — twelve specialist service lines",
        "Enterprise360 — twelve end-to-end value streams",
        "The Delivery Matrix — which service line powers each value stream",
        "Evidence & assurance — why the output holds up",
        "What we deliberately do not do — registry decisions",
      ];
      injectJsonLd("system-beats", {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "The Ekselens System of Proof — how it works",
        description: "One discipline, two methodologies and one system of proof, explained in seven beats.",
        url,
        itemListElement: beats.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b,
        })),
      });
    } else {
      removeJsonLd("system-beats");
    }

    // JSON-LD: BreadcrumbList — every non-home page gets one
    if (pathname !== "/") {
      const segments = pathname.split("/").filter(Boolean);
      const items = [{ name: "Home", url: SITE_URL + "/" }];
      let acc = "";
      segments.forEach((seg, i) => {
        acc += "/" + seg;
        // Friendly label: prefer service display name on /services/{slug},
        // otherwise capitalise + replace hyphens.
        let label = seg.replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
        if (segments[0] === "services" && i === 1) {
          const svc = services.find((x) => x.slug === seg);
          if (svc?.name) label = svc.name;
        }
        if (segments[0] === "resources" && i === 1 && seg.endsWith("-self-assessment")) {
          const brand = seg.replace(/-self-assessment$/, "").toUpperCase();
          label = `${brand} Self-Assessment`;
        }
        items.push({ name: label, url: SITE_URL + acc });
      });
      injectJsonLd("breadcrumbs", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          name: it.name,
          item: it.url,
        })),
      });
    } else {
      removeJsonLd("breadcrumbs");
    }
    } catch (e) {
      // Never let SEO injection break the page render.
      // eslint-disable-next-line no-console
      console.warn("SeoHead failed:", e);
    }
  }, [pathname, url, dynamicMeta, ogOverrides]);

  return null;
};

export default SeoHead;
